import React from "react";
import DashboardCalculationHover from "./DashboardCalculationHover.jsx";
import DashboardReportActions from "./DashboardReportActions.jsx";
import { chartRatio } from "../utils/dashboardChartGeometry.js";
import { resolveComparisonLabel, resolveMetricDelta } from "../utils/dashboardComparison.js";

const COUNT = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });
const count = (value) => (value == null ? "n/d" : COUNT.format(value));

// Three lists without duplicates, then the weekdays; each list keeps the email reports of the cards it replaces.
// `gauge` draws a 4 px bar under a percentage; `higherIsBetter: null` keeps a volume change gray (⑦).
const LISTS = [
  { key: "truck", title: "Camion moyen", reportKeys: ["truck_average", "client_visits"], rows: [
    { label: "Camions utilisés / actifs", text: (s) => `${count(s.usedTruckCount)} / ${count(s.activeTruckCount)}`, value: (s) => s.usedFleetPct, unit: "pts", higherIsBetter: true },
    { label: "Tonnes / camion", text: (s) => s.avgTonnagePerUsedTruckLabel, value: (s) => s.avgTonnagePerUsedTruckTons, higherIsBetter: true },
    { label: "Km / camion", text: (s) => s.avgDistancePerUsedTruckLabel, value: (s) => s.avgDistancePerUsedTruckKm, higherIsBetter: false },
    { label: "Arrêts / camion", text: (s) => s.avgBusinessStopCountLabel, value: (s) => s.avgBusinessStopCountPerUsedTruck, higherIsBetter: null },
    { label: "Voyages / camion actif", text: (s) => s.avgDeliveryVisitCountPerActiveTruckLabel, value: (s) => s.avgDeliveryVisitCountPerActiveTruck, higherIsBetter: true },
  ] },
  { key: "loading", title: "Chargement", reportKeys: ["compartments"], rows: [
    { label: "Compartiments utilisés", gauge: true, text: (s) => s.avgCompartmentUsageLabel, value: (s) => s.avgCompartmentUsagePct, unit: "pts", higherIsBetter: true },
    { label: "Remplissage des compartiments", gauge: true, text: (s) => s.avgCompartmentFillLabel, value: (s) => s.avgCompartmentFillPct, unit: "pts", higherIsBetter: true },
    { label: "Temps route chargé", gauge: true, text: (s) => s.avgHighLoadDriveShareLabel, value: (s) => s.avgHighLoadDriveSharePct, unit: "pts", higherIsBetter: true },
    { label: "Tonnes / client", text: (s) => s.avgTonsPerDeliveryStopLabel, value: (s) => s.avgTonsPerDeliveryStopTons, higherIsBetter: true },
  ] },
  { key: "structure", title: "Structure du dispatch", reportKeys: ["dispatch_structure"], rows: [
    { label: "Préchargement des camions", gauge: true, text: (s) => s.preloadTruckLabel, value: (s) => s.preloadTruckPct, unit: "pts", higherIsBetter: true },
    { label: "Heure + priorité", gauge: true, text: (s) => s.priorityWindowTruckLabel, value: (s) => s.priorityWindowTruckPct, unit: "pts", higherIsBetter: false },
    { label: "Commandes protégées", gauge: true, text: (s) => s.protectedOrderLabel, value: (s) => s.protectedOrderPct, unit: "pts", higherIsBetter: true },
    { label: "Commandes / dispatch", text: (s) => s.avgDispatchOrderCountLabel, value: (s) => s.avgDispatchOrderCount, higherIsBetter: null },
  ] },
];

function MetricRow({ row, period, summary, previousSummary }) {
  const text = row.text(summary) ?? "n/d";
  const ratio = row.gauge ? chartRatio(row.value(summary)) : null;
  const delta = previousSummary ? resolveMetricDelta(row.value(summary), row.value(previousSummary), row) : null;
  const rows = [[period.key === "day" ? "Hier" : "Période", text], ...(previousSummary ? [["Période précédente", row.text(previousSummary) ?? "n/d"]] : [])];
  return <DashboardCalculationHover label={row.label} value={text} hoverCard={{ content: <dl className="dashboard-kpi-rows">
    {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    {delta ? <div><dt>Écart</dt><dd>{delta.label} {resolveComparisonLabel(period, previousSummary.dispatchDayCount)}</dd></div> : null}
  </dl> }}>
    <div className="dashboard-metric-row" data-dashboard-metric-row={row.label}>
      <span className="dashboard-metric-label">{row.label}
        {ratio !== null ? <span className="dashboard-metric-gauge" aria-hidden="true"><span style={{ width: `${ratio * 100}%` }} /></span> : null}
      </span>
      <span className="dashboard-metric-value">{text}</span>
      <span className="dashboard-metric-delta" data-tone={delta?.tone}>{delta?.label ?? ""}</span>
    </div>
  </DashboardCalculationHover>;
}

const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"];
const TONNES = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 0 });

// Tonnage per dispatch for each weekday of the chosen period, its gauge relative to the busiest day; « — » without a dispatch.
function WeekdayList({ period }) {
  const groups = WEEKDAYS.map((name, index) => {
    const days = (period.days || []).filter((day) => day.hasDispatch && day.dispatchDate
      && new Date(`${day.dispatchDate.slice(0, 10)}T12:00:00Z`).getUTCDay() === index + 1);
    const average = (field) => (days.length ? days.reduce((total, day) => total + (day[field] || 0), 0) / days.length : null);
    return { name, count: days.length, tons: average("plannedTonnageTons"), orders: average("dispatchOrderCount"), trucks: average("usedTruckCount") };
  });
  const peak = Math.max(...groups.map((group) => group.tons || 0));
  return <section className="dashboard-card dashboard-metric-list dashboard-weekday-list rounded-[4px]" aria-label="Par jour de semaine">
    <header className="dashboard-card-header"><div><h2>Par jour de semaine</h2><p>{period.key === "day" ? "Hier" : "Tonnage moyen / dispatch"}</p></div></header>
    <div className="dashboard-weekday-grid">{groups.map((group) => {
      const text = group.tons == null ? "—" : `${TONNES.format(group.tons)} t`;
      const rows = [["Dispatchs", String(group.count)], ["Tonnage moyen", text], ["Commandes", count(group.orders)], ["Camions utilisés", count(group.trucks)]];
      return <DashboardCalculationHover key={group.name} label={group.name} value={text} hoverCard={group.count ? { content: <dl className="dashboard-kpi-rows">
        {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl> } : null}>
        <div className="dashboard-weekday" data-dashboard-weekday={group.name}>
          <span className="dashboard-metric-label">{group.name}</span>
          <span className="dashboard-metric-value">{text}</span>
          <span className="dashboard-metric-gauge" aria-hidden="true"><span style={{ width: `${peak ? ((group.tons || 0) * 100) / peak : 0}%` }} /></span>
        </div>
      </DashboardCalculationHover>;
    })}</div>
  </section>;
}

export default function DashboardMetricList({ period, summary, previousSummary = null }) {
  return <div className="dashboard-metric-lists">
    {LISTS.map((list) => <section key={list.key} className="dashboard-card dashboard-metric-list rounded-[4px]" aria-label={list.title}>
      <header className="dashboard-card-header">
        <div><h2>{list.title}</h2><p>{period.key === "day" ? "Hier" : "Moyenne / dispatch"}</p></div>
        <div className="dashboard-card-actions"><DashboardReportActions title={list.title} keys={list.reportKeys} /></div>
      </header>
      <div className="dashboard-metric-rows">{list.rows.map((row) => <MetricRow key={row.label} row={row} period={period} summary={summary} previousSummary={previousSummary} />)}</div>
    </section>)}
    <WeekdayList period={period} />
  </div>;
}
